import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Percent, Loader2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors PlatformSettings.infinity_price_multiplier's schema default, so the
// control shows the value the backend would actually use before the platform
// doc is ever saved.
const DEFAULT_MULTIPLIER = 2.5;
const MIN = 1;
const MAX = 5;
const STEP = 0.01;

// A base price to make the effect concrete. Arbitrary but stable — the point is
// the arithmetic, not the product.
const EXAMPLE_BASE = 21;

/**
 * Super-admin control for the Infinity Touch retail multiplier.
 *
 * Unlike eDisplayroom — where each funeral home sets its own
 * `store.global_price_multiplier` — Infinity Touch pricing is set ONCE here and
 * applies to every funeral home (TR-22). The per-FH IT slider that used to live
 * in Reseller Settings is gone; this page replaces it.
 *
 * Only the customer-facing price moves. Funeral homes are always billed the
 * product's base price on IT orders, so raising this never changes what an FH pays.
 */
export default function AdminInfinityPriceMultiplier() {
  const [multiplier, setMultiplier] = useState(DEFAULT_MULTIPLIER);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });

  useEffect(() => {
    (async () => {
      try {
        const settings = await axios.get(`${API}/admin/platform-settings`, authHeaders());
        const saved = Number(settings.data?.infinity_price_multiplier);
        setMultiplier(Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_MULTIPLIER);
      } catch {
        toast.error("Failed to load the price multiplier");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onChange = (value) => {
    setMultiplier(value);
    setHasChanges(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Round to the slider's own precision before persisting. A 0.01 step lands on
      // values like 2.5500000000000003, and this number multiplies every customer-facing
      // IT price — the stored value must be exactly what the admin was shown.
      const value = Number(multiplier.toFixed(2));
      await axios.put(`${API}/admin/platform-settings`, { infinity_price_multiplier: value }, authHeaders());
      toast.success("Price multiplier saved");
      setHasChanges(false);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const customerPays = EXAMPLE_BASE * multiplier;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Percent className="w-6 h-6" />
            Price Multiplier
            {hasChanges ? <Badge variant="secondary" className="ml-2">Unsaved changes</Badge> : null}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            The retail markup applied to Infinity Touch products for every funeral home.
          </p>
        </div>
        <Button onClick={save} disabled={saving || !hasChanges} className="h-11">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      <div className="bg-white border rounded-2xl p-6 space-y-5 max-w-2xl">
        <div className="space-y-3">
          <Label>Infinity Touch Price Multiplier</Label>
          <div className="flex items-center gap-4">
            <Slider
              value={[multiplier]}
              onValueChange={(value) => onChange(value[0])}
              min={MIN}
              max={MAX}
              step={STEP}
              className="flex-1"
            />
            <span className="w-20 text-center font-mono text-lg font-semibold text-primary">
              {multiplier.toFixed(2)}x
            </span>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 text-sm">
          <p className="text-slate-500">For example, on a product with a base price of ${EXAMPLE_BASE.toFixed(2)}:</p>
          <p className="mt-2 text-slate-800">
            the family pays <span className="font-semibold">${customerPays.toFixed(2)}</span>, and the funeral home
            keeps <span className="font-semibold">${(customerPays - EXAMPLE_BASE).toFixed(2)}</span> as commission.
          </p>
        </div>

        <p className="text-xs text-slate-500">
          This applies to every funeral home on Infinity Touch. It only affects what families pay on the
          customer site — funeral homes are always billed the base price on their own orders, and products
          with a fixed Infinity price override ignore this multiplier entirely.
          e-Displayroom pricing is separate and stays per funeral home.
        </p>
      </div>
    </div>
  );
}
